import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { useActivities } from '../hooks/useActivities';
import Calendar from '../components/Calendar';
import ActivityList from '../components/ActivityList';
import Layout from '../components/Layout';

export default function Activities() {
  const { user } = useAuth();
  const userId = user ? user.uid : null;
  const { activities, loading, error: activitiesError } = useActivities(userId);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [componentError, setComponentError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('Activities component mounted');
    console.log('Auth state:', { user });
    console.log('Activities loading:', loading);
    console.log('Activities error:', activitiesError);

    if (activities && activities.length > 0) {
      setComponentError(null);
    }
  }, [user, loading, activitiesError, activities]);

  useEffect(() => {
    try {
      if (!loading && (!activities || activities.length === 0)) {
        console.warn('No activities found');
        setComponentError('No activities available');
      }
    } catch (err) {
      console.error('Error in Activities component:', err);
      setComponentError('Failed to load activities');
    }
  }, [activities, loading, activitiesError]);

  if (loading) {
    console.log('Loading state in Activities');
    return (
      <Layout>
        <div>Loading activities...</div>
      </Layout>
    );
  }

  if (activitiesError) {
    console.error('Error loading activities:', activitiesError);
    return (
      <Layout>
        <div>Error loading activities. Please try again later.</div>
      </Layout>
    );
  }

  if (!activities) {
    console.warn('Activities data is undefined');
    return (
      <Layout>
        <div>No activities data available.</div>
      </Layout>
    );
  }

  if (componentError) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center text-red-600">
          <h2 className="text-2xl font-bold mb-4">Oops! Something went wrong</h2>
          <p>{componentError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded mr-2"
          >
            Reload
          </button>
          <button 
            onClick={() => navigate('/activity/new')}
            className="mt-4 px-4 py-2 bg-green-500 text-white rounded"
          >
            Add New Activity
          </button>
        </div>
      </Layout>
    );
  }

  if (!activities || activities.length === 0) {
    console.warn('No activities available');
    return <Layout><div>No activities available.</div></Layout>;
  }

  const currentActivities = activities.filter(activity => {
    const activityDate = new Date(activity.date);
    return (
      activityDate.getDate() === selectedDate.getDate() &&
      activityDate.getMonth() === selectedDate.getMonth() &&
      activityDate.getFullYear() === selectedDate.getFullYear()
    );
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="sm:flex sm:items-center mb-6">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Activities</h1>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <Link
              to="/activity/new"
              className="inline-flex items-center rounded-md bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              New Activity
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <Calendar selectedDate={selectedDate} onSelectDate={setSelectedDate} activities={activities} />
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                  Activities for {selectedDate.toLocaleDateString('default', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </h2>
              </div>
              <ActivityList activities={currentActivities} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}